// eslint-disable-next-line no-restricted-imports
import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Pagination,
  Table,
  TableColumn,
  Tag,
  Text,
  useToasts,
} from '@applyboard/crystal-ui';
import { CloseOutlineIcon } from '@applyboard/ui-icons';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { VasError } from '../../common/errors/VasError';
import { VasSpinner } from '../../components/Spinner';
import StudentSearchCombobox, { StudentDetails } from '../../components/StudentSearch';
import {
  RawTestVoucher,
  useAssignVoucherToStudent,
  useUnassignVoucher,
} from '../../data/testVouchers';
import { PurchaseTestVouchersModal } from './PurchaseTestVouchersModal';
import VoucherActions from './VoucherActions';
import normalizeVoucherType from './normalizeVoucherType';
import { STATUS_LABEL_MAP, VOUCHER_TYPE_LABEL_MAP } from './types';

export const FilterFields = ['status', 'type'];
export type FilterFieldsType = (typeof FilterFields)[number];

export const VoucherTableEvents = ['pageSize', 'currentPage', 'assigned', 'unassigned'];
export type VoucherTableEventsType = (typeof VoucherTableEvents)[number];

interface VoucherTableProps {
  onChange?: (event: VoucherTableEventsType, value: unknown) => Promise<void>;
  testVouchers?: RawTestVoucher[];
  totalTestVoucher?: number;
  loading: boolean;
}

function VoucherTable({ testVouchers, loading, totalTestVoucher, onChange }: VoucherTableProps) {
  const toast = useToasts();
  const [voucherLoadingStatusMap, setVoucherLoadingStatusMap] = useState<Record<string, boolean>>(
    {},
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { assignVoucherToStudent } = useAssignVoucherToStudent();
  const { unassignVoucher } = useUnassignVoucher();
  const { trackEvent } = useEventTracker();
  const [currentUser] = useCurrentUser();

  const setSafeVoucherLoadingStatus = useCallback(
    (voucherId: string, loading: boolean) => {
      setVoucherLoadingStatusMap(value => ({
        ...value,
        [voucherId]: loading,
      }));
    },
    [setVoucherLoadingStatusMap],
  );

  // Use callback is needed because we're using this inside a memoized data
  const renderStudentCell = useCallback(
    (voucher: RawTestVoucher) => {
      if (voucherLoadingStatusMap[voucher.id]) {
        return <VasSpinner />;
      }

      const studentName =
        `${voucher.attributes.student_first_name ?? ''} ${voucher.attributes.student_last_name ?? ''}`.trim();

      const isStudentDetailsAvailable = !!studentName || !!voucher.attributes.student_email;
      if (isStudentDetailsAvailable) {
        return (
          <Table.Cell>
            <Flex direction="row" align="center" gap={4}>
              <Button
                aria-label="unassign-student"
                leadIcon={CloseOutlineIcon}
                emphasis="transparent"
                intent="secondary"
                size="sm"
                width="hug"
                type="button"
                onClick={() => {
                  setSafeVoucherLoadingStatus(voucher.id, true);
                  unassignVoucher(
                    voucher.id,
                    async () => {
                      await onChange?.('unassigned', voucher.id);
                      setSafeVoucherLoadingStatus(voucher.id, false);
                      trackEvent({
                        event:
                          'VAS - Vas Hub Page - Voucher Management - Unassign student to voucher',
                        data: {
                          rp_id: currentUser?.id,
                          voucher_type: normalizeVoucherType(voucher.attributes.type),
                        },
                      });
                    },
                    error => {
                      setSafeVoucherLoadingStatus(voucher.id, false);
                      console.error('failed to unassign voucher', error);
                      toast.negative(
                        new Error('Unable to unassign voucher, please try again later'),
                      );
                    },
                  );
                }}
              />
              <Flex direction="column">
                <Text variant="titleS" intent="secondary">
                  {studentName}
                </Text>
                <Text variant="bodyM">{voucher.attributes.student_email}</Text>
              </Flex>
            </Flex>
          </Table.Cell>
        );
      } else {
        return (
          <StudentSearchCombobox
            onChange={(studentDetails: StudentDetails) => {
              setSafeVoucherLoadingStatus(voucher.id, true);
              assignVoucherToStudent(
                {
                  studentId: studentDetails.studentId,
                  voucherId: voucher.id,
                },
                async () => {
                  await onChange?.('assigned', voucher.id);
                  setSafeVoucherLoadingStatus(voucher.id, false);
                  trackEvent({
                    event: 'VAS - Vas Hub Page - Voucher Management - Assign student to voucher',
                    data: {
                      rp_id: currentUser?.id,
                      voucher_type: normalizeVoucherType(voucher.attributes.type),
                      is_new_student: studentDetails.isNewStudent,
                    },
                  });
                },
                error => {
                  setSafeVoucherLoadingStatus(voucher.id, false);
                  console.error('Failed to assign student to voucher', error);

                  let errorMessage = 'Failed to assign student to voucher. Please try again later.';
                  const errorBody = error instanceof VasError ? error.errorBody : null;
                  if (errorBody && errorBody.statusCode === 400) {
                    errorMessage = errorBody.message[0];
                  }

                  toast.negative(new Error(errorMessage));
                },
              );
            }}
          />
        );
      }
    },
    [
      assignVoucherToStudent,
      toast,
      voucherLoadingStatusMap,
      unassignVoucher,
      setSafeVoucherLoadingStatus,
      onChange,
      trackEvent,
      currentUser,
    ],
  );

  const columns = useMemo<TableColumn<RawTestVoucher>[]>(
    () => [
      {
        accessorKey: 'id',
        cell: (props: RawTestVoucher) => (
          <Table.TextCell>{props.attributes.voucher_code}</Table.TextCell>
        ),
        header: 'Voucher Id',
        divider: true,
      },
      {
        accessorKey: 'testType',
        cell: (props: RawTestVoucher) => (
          <Table.TextCell>{VOUCHER_TYPE_LABEL_MAP[props.attributes.type]}</Table.TextCell>
        ),
        header: 'Test Type',
      },
      {
        accessorKey: 'student',
        cell: renderStudentCell,
        header: 'Student',
      },
      {
        accessorKey: 'status',
        cell: (props: RawTestVoucher) => (
          <Table.Cell>
            <Tag
              emphasis="highlighted"
              intent={props.attributes.status === 'assigned' ? 'positive' : 'primary'}
              size="lg"
            >
              {STATUS_LABEL_MAP[props.attributes.status]}
            </Tag>
          </Table.Cell>
        ),
        header: 'Status',
      },
      {
        accessorKey: 'createdAt',
        cell: (props: RawTestVoucher) => (
          <Table.TextCell>
            {new Date(props.attributes.created_at).toLocaleDateString()}
          </Table.TextCell>
        ),
        header: 'Created',
      },
      {
        accessorKey: 'expiresAt',
        cell: (props: RawTestVoucher) => (
          <Table.TextCell>
            {new Date(props.attributes.expires_at).toLocaleDateString()}
          </Table.TextCell>
        ),
        header: 'Expiry Date',
      },
      {
        accessorKey: 'actions',
        cell: (props: RawTestVoucher) => (
          <Table.Cell>
            <VoucherActions voucher={props} disabled={props.attributes.status === 'unassigned'} />
          </Table.Cell>
        ),
        header: 'Actions',
      },
    ],
    [renderStudentCell],
  );

  return (
    <>
      <Table columns={columns} data={testVouchers ?? []} loading={loading}>
        <Table.NoData>
          <Flex justify="center" width="fill">
            <PurchaseTestVouchersModal />
          </Flex>
        </Table.NoData>
      </Table>
      <Pagination
        currentPage={currentPage}
        numItems={totalTestVoucher || 0}
        onPageChange={(value: number) => {
          setCurrentPage(value);
          onChange?.('currentPage', value);
        }}
        onPageSizeChange={(value: number) => {
          setPageSize(value);
          onChange?.('pageSize', value);
        }}
        pageSize={pageSize}
        pageSizeOptions={[5, 10, 20, 50]}
        variant="items"
      />
    </>
  );
}

export default VoucherTable;
